<template>
  <ul class="tree-node">
    <!-- INITIAL SELECTION MODE - Show when block has no type -->
    <li v-if="!block.type" class="tree-leaf selection-mode">
      <div class="selection-container">
        <el-button type="primary" size="mini" plain @click="setType('ACTION')" class="type-btn">
          ⚙️ Action
        </el-button>
        <el-button type="success" size="mini" plain @click="setType('CONDITION')" class="type-btn">
          🔀 Condition
        </el-button>
        <el-button
          type="danger"
          size="mini"
          icon="el-icon-delete"
          @click="confirmRemove"
          circle
          class="remove-btn"
          title="Remove Block"
        />
      </div>
    </li>

    <!-- ACTION BLOCK -->
    <li
      v-else-if="block.type === 'ACTION'"
      class="tree-leaf action-block"
      :class="{ 'invalid-action': isActionInvalid(block) }"
    >
      <div class="action-header">
        <span class="action-number">{{ actionNumber }}</span>
        <ActionSummaryChip :actionKey="block.action || ''" :label="block.label" />
        <!-- ✏️ Edit Icon Button -->
        <el-button
          type="info"
          size="mini"
          icon="el-icon-edit"
          circle
          class="edit-btn"
          title="Edit Action"
          @click="openEditDrawer"
        />
        <el-button
          type="danger"
          size="mini"
          icon="el-icon-delete"
          @click="confirmRemove"
          circle
          class="remove-btn"
          title="Remove Action"
        />
      </div>
    </li>

    <!-- CONDITION BLOCK -->
    <li v-else-if="block.type === 'CONDITION'" class="tree-branch condition-block">
      <div class="condition-header">
        <span class="action-number">{{ actionNumber }}</span>
        <span class="icon">🔀</span>
        <span class="label">Condition:</span>

        <el-button
          type="danger"
          size="mini"
          icon="el-icon-delete"
          @click="confirmRemove"
          circle
          class="remove-btn"
          title="Remove Condition"
        />
      </div>

      <!-- Show branches only when a condition is selected -->
      <div>
        <!-- IF Branch -->
        <div class="branch">
          <div class="branch-label">
            <span class="branch-number">IF</span>
            <el-select
              v-model="block.filter_id"
              :disabled="block.conditions ? true : false"
              placeholder="Select Existing Filter"
              size="mini"
              filterable
              clearable
              style="width: 220px"
            >
              <el-option
                v-for="filter in entityAllFilters"
                :key="filter._id"
                :label="filter.filter_name"
                :value="filter._id"
              >
                {{ filter.filter_name }}
              </el-option>
            </el-select>
            <span class="ml-2">or</span>
            <template v-if="!block.conditions">
              <el-button
                :disabled="block.filter_id ? true : false"
                type="text"
                size="mini"
                @click="openConditionalModal('IF')"
              >
                Add Conditions
              </el-button>
            </template>
            <template v-else>
              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-600 font-medium px-2 py-1 bg-gray-100 rounded">
                  {{ block.conditions.name }}
                </span>
                <i
                  class="el-icon-edit text-blue-600 cursor-pointer hover:text-blue-800"
                  @click="openConditionalModal('IF')"
                ></i>
                <i
                  class="el-icon-circle-close text-red-500 cursor-pointer hover:text-red-700 ml-1"
                  @click="removeConditions('IF')"
                ></i>
              </div>
            </template>
            <span class="ml-2 branch-action-count">
              ({{ getBranchActionCount('then') }} actions)
            </span>
          </div>
          <div class="branch-content">
            <ActionComponent
              v-for="(child, idx) in block.then"
              :key="'then-' + idx"
              :block="child"
              :action-number="getNestedActionNumber('then', idx)"
              :currentEntity="currentEntity"
              :trigger="trigger"
              :entityAllFilters="entityAllFilters"
              :allEntities="allEntities"
              :allEntityFilters="allEntityFilters"
              :allFields="allFields"
              :allContacts="allContacts"
              :companyUsers="companyUsers"
              :allTemplates="allTemplates"
              :templateFieldMap="templateFieldMap"
              :allTemplateFieldMap="allTemplateFieldMap"
              :companyAllTemplateFields="companyAllTemplateFields"
              :allFormbuilders="allFormbuilders"
              :entityRelationships="entityRelationships"
              :randomStr="randomStr"
              @remove="removeThenAction(idx)"
            />
            <el-button
              type="primary"
              size="mini"
              plain
              icon="el-icon-plus"
              @click="addThen"
              class="add-action-btn"
            >
              Add Action
            </el-button>
          </div>
        </div>

        <!-- ELSE IF Branches -->
        <div class="branch" v-for="(elseif, idx) in block.elseIf" :key="'elseif-' + idx">
          <div class="branch-label">
            <span class="branch-number">ELSE IF</span>
            <el-select
              v-model="elseif.filter_id"
              :disabled="elseif.conditions ? true : false"
              placeholder="Select Existing Filter"
              size="mini"
              filterable
              clearable
              style="width: 220px"
            >
              <el-option
                v-for="filter in entityAllFilters"
                :key="filter._id"
                :label="filter.filter_name"
                :value="filter._id"
              >
                {{ filter.filter_name }}
              </el-option>
            </el-select>
            <span class="ml-2">or</span>
            <template v-if="!elseif.conditions">
              <el-button
                :disabled="elseif.filter_id ? true : false"
                type="text"
                size="mini"
                @click="openConditionalModal('ELSEIF', idx)"
              >
                Add Conditions
              </el-button>
            </template>
            <template v-else>
              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-600 font-medium px-2 py-1 bg-gray-100 rounded">
                  {{ elseif.conditions.name }}
                </span>
                <i
                  class="el-icon-edit text-blue-600 cursor-pointer hover:text-blue-800"
                  @click="openConditionalModal('ELSEIF', idx)"
                ></i>
                <i
                  class="el-icon-circle-close text-red-500 cursor-pointer hover:text-red-700 ml-1"
                  @click="removeConditions('ELSEIF', idx)"
                ></i>
              </div>
            </template>
            <span class="branch-action-count">({{ getElseIfActionCount(idx) }} actions)</span>
            <el-button
              type="danger"
              size="mini"
              icon="el-icon-delete"
              @click="confirmRemoveElseIf(idx)"
              circle
              class="remove-btn"
              title="Remove Else If"
            />
          </div>
          <div class="branch-content" v-if="elseif.filter_id || elseif.conditions">
            <ActionComponent
              v-for="(child, cid) in elseif.then"
              :key="'elseif-child-' + cid"
              :block="child"
              :action-number="getNestedActionNumber('elseIf', idx, cid)"
              :currentEntity="currentEntity"
              :allEntities="allEntities"
              :allEntityFilters="allEntityFilters"
              :allFields="allFields"
              :allContacts="allContacts"
              :companyUsers="companyUsers"
              :trigger="trigger"
              :entityAllFilters="entityAllFilters"
              :allTemplates="allTemplates"
              :templateFieldMap="templateFieldMap"
              :allTemplateFieldMap="allTemplateFieldMap"
              :companyAllTemplateFields="companyAllTemplateFields"
              :allFormbuilders="allFormbuilders"
              :entityRelationships="entityRelationships"
              :randomStr="randomStr"
              @remove="removeElseIfAction(idx, cid)"
            />
            <el-button
              type="primary"
              size="mini"
              plain
              icon="el-icon-plus"
              @click="() => addElseIfAction(idx)"
              class="add-action-btn"
            >
              Add Action
            </el-button>
          </div>
        </div>

        <!-- ELSE Branch -->
        <div class="branch" v-if="block.else && block.else.length > 0">
          <div class="branch-label">
            <span class="branch-number">ELSE</span>
            <span class="branch-action-count">({{ getBranchActionCount('else') }} actions)</span>
            <el-button
              type="danger"
              size="mini"
              icon="el-icon-delete"
              @click="confirmRemoveElse"
              circle
              class="remove-btn"
              title="Remove Else"
            />
          </div>
          <div class="branch-content">
            <ActionComponent
              v-for="(child, idx) in block.else"
              :key="'else-' + idx"
              :block="child"
              :action-number="getNestedActionNumber('else', idx)"
              :currentEntity="currentEntity"
              :allEntities="allEntities"
              :allEntityFilters="allEntityFilters"
              :allFields="allFields"
              :allContacts="allContacts"
              :companyUsers="companyUsers"
              :allTemplates="allTemplates"
              :templateFieldMap="templateFieldMap"
              :allTemplateFieldMap="allTemplateFieldMap"
              :companyAllTemplateFields="companyAllTemplateFields"
              :allFormbuilders="allFormbuilders"
              :trigger="trigger"
              :entityAllFilters="entityAllFilters"
              :entityRelationships="entityRelationships"
              :randomStr="randomStr"
              @remove="removeElseAction(idx)"
            />
            <el-button
              type="primary"
              size="mini"
              plain
              icon="el-icon-plus"
              @click="addElse"
              class="add-action-btn"
            >
              Add Action
            </el-button>
          </div>
        </div>

        <!-- Controls -->
        <div class="condition-controls">
          <el-button
            type="success"
            size="mini"
            plain
            icon="el-icon-plus"
            @click="addElseIf"
            :disabled="!block.filter_id && !block.conditions"
          >
            Add Else If
          </el-button>
          <el-button
            type="warning"
            size="mini"
            plain
            icon="el-icon-plus"
            @click="addElse"
            :disabled="(!block.filter_id && !block.conditions) || block.else.length > 0"
          >
            Add Else
          </el-button>
          <span class="total-actions-count">Total Actions: {{ getTotalActionsCount() }}</span>
        </div>
      </div>
    </li>
    <!-- Edit Drawer -->
    <el-drawer
      title="Edit Action"
      :visible.sync="isDrawerOpen"
      direction="rtl"
      size="50%"
      append-to-body
      class="professional-drawer"
    >
      <div v-if="block.action" class="selected-action-header">
        <el-avatar :icon="getActionIcon(block.action)" size="small" style="margin-right: 8px" />
        <span class="selected-action-label">{{ getActionLabel(block.action) }}</span>
        <el-button
          type="warning"
          size="mini"
          icon="el-icon-edit"
          @click="changeAction"
          style="margin-left: auto"
        >
          Change Action
        </el-button>
      </div>

      <div v-if="!block.action">
        <ActionsStep
          v-if="currentEntity"
          :block="block"
          :trigger="trigger"
          :currentEntity="currentEntity"
          @action-selected="handleActionSelect()"
        ></ActionsStep>
      </div>
      <div v-else class="action-config-base">
        <ActionConfigurationBase
          :step="block"
          :allFields="allFields"
          :currentEntity="currentEntity"
          :allEntities="allEntities"
          :allEntityFilters="allEntityFilters"
          :trigger="trigger"
          :allContacts="allContacts"
          :companyUsers="companyUsers"
          :allTemplates="allTemplates"
          :templateFieldMap="templateFieldMap"
          :allTemplateFieldMap="allTemplateFieldMap"
          :companyAllTemplateFields="companyAllTemplateFields"
          :allFormbuilders="allFormbuilders"
          :entityAllFilters="entityAllFilters"
          :entityRelationships="entityRelationships"
          @close="closeEditDrawer"
        />
      </div>
    </el-drawer>
    <el-drawer
      title="Add Conditions"
      :visible.sync="conditionsModal"
      :direction="'rtl'"
      class="layout-drawer"
      :with-header="true"
      :modal="true"
      :append-to-body="true"
      :z-index="3000"
      :size="getIsMobile ? '80%' : '50%'"
      :before-close="closeConditionsModal"
    >
      <div class="p-1">
        <span class="warning">
          <b>Note:</b>
          The filter will apply only when the following conditions are satisfied.
        </span>
        <FilterConditions
          :currentFilter="currentConditionalFilter"
          :allFields="allFields"
          :fromFilters="true"
          :formQuickActions="false"
          :fromEntityViews="false"
          :fromScheduling="false"
          :isHierarchyObject="{}"
          :showName="true"
          @addNewCondition="addNewCondition"
          @deleteCondition="deleteCondition"
        ></FilterConditions>
      </div>
    </el-drawer>

    <dialog-component
      title="External Sources"
      :visible="openExtenalSources"
      :isShowFooter="false"
      :containerWidth="'40%'"
      :containerHeight="'55%'"
      @before-close="closeExtenalSourceDailog"
      class="high-z-index-dialog"
    >
      <SourcesListComponent
        :applications="applications"
        :params="externalSourceConfiguration"
        :integrations="allConnections"
        @openMapping="openMapping"
        @closeSourcesList="closeSourcesList"
        :entityAllFilters="entityAllFilters"
      ></SourcesListComponent>
    </dialog-component>
    <dialog-component
      title="Map Fields"
      :visible="enableMapping"
      :isShowFooter="true"
      :containerWidth="'40%'"
      :containerHeight="'55%'"
      @before-close="closeMappingConfig"
      class="high-z-index-dialog"
    >
      <ExternalSourceMapping
        :params="externalSourceConfiguration"
        :sourceFields="sourceFields"
        :targetFields="targetFields"
        :applications="applications"
        :mappingConfigLoading="mappingConfigLoading"
      ></ExternalSourceMapping>
      <span slot="footer" class="dialog-footer">
        <el-button type="plain" @click="closeMappingConfig">
          <span>Cancel</span>
        </el-button>
        <el-button type="primary" @click="validateAndSave">
          <i class="el-icon-finished"></i>
          <span>Save</span>
        </el-button>
      </span>
    </dialog-component>
  </ul>
</template>

<script>
  import { postAPICall } from '../../../helpers/httpHelper';
  import { availableActions } from './AvailableActions';
  import { mapGetters } from 'vuex';
  export default {
    name: 'ActionComponent',
    props: {
      block: Object,
      actionNumber: {
        type: [Number, String],
        default: String,
      },
      trigger: Object,
      currentEntity: Object,
      allEntities: Array,
      allEntityFilters: Object,
      entityAllFilters: Array,
      allFields: Array,
      allContacts: Array,
      companyUsers: Array,
      allTemplates: Array,
      templateFieldMap: Object,
      allTemplateFieldMap: Object,
      companyAllTemplateFields: Object,
      allFormbuilders: Array,
      entityRelationships: Array,
      randomStr: Function,
    },
    components: {
      ActionsStep: () => import('@/components/entity/actions/ActionTypes.vue'),
      ActionConfigurationBase: () =>
        import('@/components/entity/actions/configuration/ActionConfigurationBase.vue'),
      ActionSummaryChip: () => import('@/components/entity/actions/ActionSummaryChip.vue'),
      SourcesListComponent: () => import('@/components/entity/sourcesListComponent.vue'),
      FilterConditions: () => import('@/components/widgets/FilterConditions.vue'),
    },
    data() {
      return {
        conditionsModal: false,
        isDrawerOpen: false,
        selectedIndex: -1,
        selectedActionType: '',
        currentConditionalFilter: null,
        externalSourceConfiguration: {
          application: '',
          integration: '',
          event: '',
          importable: false,
          exportable: false,
          form: {},
          mappingConfig: {},
          filter: '',
        },
        applications: [],
        openExtenalSources: false,
        allConnections: [],
        sourceFields: [],
        targetFields: [],
        enableMapping: false,
        mappingConfigLoading: false,
        allActions: [
          ...availableActions.enhancedSelfEntityOptions.map((e) => {
            if (!e.label.includes('Self')) e.label = 'Self ' + e.label;
            return e;
          }),
          ...availableActions.enhancedOtherEntityOptions.map((e) => {
            if (!e.label.includes('Other')) e.label = 'Other ' + e.label;
            return e;
          }),
          availableActions.deleteAction,
          ...availableActions.fileManagementActions,
          ...availableActions.documentTemplateActions,
          ...availableActions.formBuilderActions,
          ...availableActions.calendarActions,
          availableActions.externalSourceAction,
          {
            ...availableActions.createContactAction,
            key: availableActions.createContactAction.key.includes('self')
              ? availableActions.createContactAction.key
              : 'self/' + availableActions.createContactAction.key,
          },
          {
            ...availableActions.deleteAction,
            key: availableActions.deleteAction.key.includes('self')
              ? availableActions.deleteAction.key
              : 'self/' + availableActions.deleteAction.key,
          },
        ].filter(Boolean),
      };
    },
    computed: {
      ...mapGetters('navigationOpen', ['getIsMobile']),
    },
    methods: {
      isActionInvalid(block) {
        return !block.action || block.valid === false;
      },
      confirmRemove() {
        this.$confirm(
          'Are you sure you want to remove this block? This action cannot be undone.',
          'Confirm Removal',
          {
            confirmButtonText: 'Yes, Remove',
            cancelButtonText: 'Cancel',
            type: 'warning',
            confirmButtonClass: 'el-button--danger',
          }
        )
          .then(() => {
            this.$emit('remove');
          })
          .catch(() => {
            // User cancelled - do nothing
          });
      },
      confirmRemoveElse() {
        const actionCount = this.getBranchActionCount('else');

        this.$confirm(
          `Are you sure you want to remove the ELSE branch? ${actionCount} action(s) will be removed.`,
          'Confirm Removal',
          {
            confirmButtonText: 'Yes, Remove',
            cancelButtonText: 'Cancel',
            type: 'warning',
            confirmButtonClass: 'el-button--danger',
          }
        )
          .then(() => {
            this.removeElse();
          })
          .catch(() => {
            // User cancelled - do nothing
          });
      },
      confirmRemoveElseIf(index) {
        this.$confirm(
          `Are you sure you want to remove the ELSE IF branch"? All actions within this branch will also be removed.`,
          'Confirm Removal',
          {
            confirmButtonText: 'Yes, Remove',
            cancelButtonText: 'Cancel',
            type: 'warning',
            confirmButtonClass: 'el-button--danger',
          }
        )
          .then(() => {
            this.removeElseIf(index);
          })
          .catch(() => {
            // User cancelled - do nothing
          });
      },

      getIconStyle(action) {
        return {
          backgroundColor: action.bgColor,
          color: action.color,
        };
      },
      setType(type) {
        this.$set(this.block, 'type', type);
        if (type === 'CONDITION') {
          this.$set(this.block, 'CONDITION', '');
          this.$set(this.block, 'then', []);
          this.$set(this.block, 'elseIf', []);
          this.$set(this.block, 'else', []);
        } else if (type === 'ACTION') {
          this.openEditDrawer();
        }
      },

      addThen() {
        this.block.then.push({
          type: 'ACTION',
          label: '',
          then: [],
          else: [],
          keyIndex: this.randomStr(10),
          entities_from: [],
        });
      },

      removeThenAction(index) {
        this.block.then.splice(index, 1);
      },

      addElseIf() {
        if (!this.block.elseIf) {
          this.$set(this.block, 'elseIf', []);
        }
        this.block.elseIf.push({
          filter_id: '',
          then: [],
        });
      },

      addElseIfAction(index) {
        this.block.elseIf[index].then.push({
          type: 'ACTION',
          label: '',
          then: [],
          else: [],
          keyIndex: this.randomStr(10),
          entities_from: [],
        });
      },

      removeElseIfAction(elseIfIndex, actionIndex) {
        this.block.elseIf[elseIfIndex].then.splice(actionIndex, 1);
      },

      removeElseIf(index) {
        this.block.elseIf.splice(index, 1);
      },

      addElse() {
        if (!this.block.else) {
          this.$set(this.block, 'else', []);
        }
        this.block.else.push({
          type: 'ACTION',
          label: '',
          then: [],
          else: [],
          keyIndex: this.randomStr(10),
          entities_from: [],
        });
      },

      removeElseAction(index) {
        this.block.else.splice(index, 1);
      },

      removeElse() {
        this.block.else = [];
      },

      // New methods for numbering and counting
      getBranchActionCount(branchType) {
        if (!this.block[branchType]) return 0;
        return this.block[branchType].length;
      },

      getElseIfActionCount(elseIfIndex) {
        if (!this.block.elseIf[elseIfIndex] || !this.block.elseIf[elseIfIndex].then) return 0;
        return this.block.elseIf[elseIfIndex].then.length;
      },

      getTotalActionsCount() {
        let count = 0;

        // Count actions in THEN branch
        if (this.block.then) {
          count += this.block.then.length;
        }

        // Count actions in ELSE IF branches
        if (this.block.elseIf) {
          this.block.elseIf.forEach((elseIf) => {
            if (elseIf.then) {
              count += elseIf.then.length;
            }
          });
        }

        // Count actions in ELSE branch
        if (this.block.else) {
          count += this.block.else.length;
        }

        return count;
      },

      getNestedActionNumber(branchType, branchIndex, actionIndex = null) {
        let baseNumber = this.actionNumber;

        if (branchType === 'then') {
          return `${baseNumber}.${branchIndex + 1}`;
        } else if (branchType === 'elseIf') {
          return `${baseNumber}.${branchIndex + 1}.${actionIndex + 1}`;
        } else if (branchType === 'else') {
          return `${baseNumber}.${branchIndex + 1}`;
        }

        return baseNumber.toString(); // Ensure it returns a string
      },

      openEditDrawer() {
        this.isDrawerOpen = true;
      },
      closeEditDrawer() {
        this.isDrawerOpen = false;
      },
      handleActionSelect() {
        if (this.block.action == 'self/DELETE') {
          this.$message.warning('Warning. Selected data will be deleted');
        } else if (this.block.action == 'external_source') {
          this.openExternalSourceConfiguration();
        }
      },
      async openExternalSourceConfiguration() {
        if (this.block.external_source_mapping_config) {
          this.externalSourceConfiguration = this.block.external_source_mapping_config;
        }
        await this.getAllSources();
        this.openExtenalSources = true;
      },
      async getAllSources() {
        let applicationsList = await postAPICall(
          'GET',
          'integrations/service/get-all-applications'
        );
        this.applications = applicationsList.data;
      },
      getActionIcon(action) {
        let currentAction = this.allActions.find((e) => e.key == action);
        return currentAction?.icon || 'el-icon-document';
      },
      getActionLabel(action) {
        let currentAction = this.allActions.find((e) => e.key == action);
        return currentAction?.label || action;
      },

      changeAction() {
        this.$confirm(
          'Changing the action will remove all existing configuration. Continue?',
          'Warning',
          {
            confirmButtonText: 'OK',
            cancelButtonText: 'Cancel',
            type: 'warning',
          }
        )
          .then(() => {
            // Reset the action data
            this.block.action = '';
            this.block.label = '';
            this.block.then = [];
            this.block.elseIf = [];
            this.block.else = [];
          })
          .catch(() => {
            // do nothing on cancel
          });
      },

      closeExtenalSourceDailog() {
        this.openExtenalSources = false;
      },
      async loadIntegrations() {
        let params = {
          limit: 10,
          page: 1,
        };
        const result = await postAPICall('GET', 'integrations', params);
        this.allConnections = result?.data || [];
      },
      async openMapping(application) {
        try {
          let event = application.events.find(
            (event) => event.eventKey === this.externalSourceConfiguration.event
          );
          this.enableMapping = true;
          let fields;
          let validRequest = false;
          let settings = event.settings;
          if (settings.dynamicResponseFields) {
            if (
              settings.fieldsRequiredToloadResponseFields &&
              settings.fieldsRequiredToloadResponseFields.length
            ) {
              validRequest = settings.fieldsRequiredToloadResponseFields.every((field) => {
                return this.externalSourceConfiguration.form[field];
              });
            } else {
              validRequest = true;
            }
            if (validRequest) {
              this.mappingConfigLoading = true;
              const result = await postAPICall(
                'POST',
                'integrations/service/getRequiredFields',
                this.externalSourceConfiguration
              );
              this.mappingConfigLoading = false;
              fields = result.data;
            }
          } else {
            fields = event.settings.availableColumns;
          }
          this.sourceFields = this.allCurrentEntityFields.map((field) => {
            return {
              applicationFieldLabel: field.label,
              applicationFieldKey: field.key,
              required: field.validations.required,
              targetField: '',
            };
          });
          this.targetFields = fields;
          this.closeExtenalSourceDailog();
        } catch (err) {
          this.mappingConfigLoading = false;
        }
      },
      openConditionalModal(type, index = -1) {
        this.selectedIndex = index;
        this.selectedActionType = type;
        if (type == 'IF' && this.block.conditions) {
          this.currentConditionalFilter = this.block.conditions;
        } else if (type == 'ELSEIF' && this.block.elseIf[index].conditions) {
          this.currentConditionalFilter = this.block.elseIf[index].conditions;
        } else {
          this.resetConditionalFilter();
        }
        this.conditionsModal = true;
      },
      closeConditionsModal() {
        if (this.selectedActionType == 'IF') {
          this.$set(this.block, 'conditions', this.currentConditionalFilter);
        } else {
          this.$set(
            this.block.elseIf[this.selectedIndex],
            'conditions',
            this.currentConditionalFilter
          );
        }
        this.resetConditionalFilter();
        this.conditionsModal = false;
      },
      removeConditions(type, index) {
        this.selectedIndex = index;
        this.selectedActionType = type;
        this.$confirm(
          'Are you sure you want to remove these conditions? This action cannot be undone.',
          'Confirm Removal',
          {
            confirmButtonText: 'Yes, Remove',
            cancelButtonText: 'Cancel',
            type: 'warning',
            confirmButtonClass: 'el-button--danger',
          }
        )
          .then(() => {
            this.resetConditions();
          })
          .catch(() => {
            // User cancelled - do nothing
          });
      },
      resetConditions() {
        if (this.selectedActionType == 'IF') {
          this.$set(this.block, 'conditions', null);
        } else {
          this.$set(this.block.elseIf[this.selectedIndex], 'conditions', null);
        }
      },
      resetConditionalFilter() {
        this.currentConditionalFilter = {
          name: 'Condition',
          c_query_type: 'AND',
          conditions: [
            {
              selectField: '',
              operator: '',
              data_source: '',
              value: '',
              startValue: '',
              endValue: '',
            },
          ],
        };
      },
      addNewCondition() {
        this.currentConditionalFilter.conditions.push({
          selectField: '',
          operator: '',
          data_source: '',
          value: '',
          startValue: '',
          endValue: '',
        });
      },
      deleteCondition(index) {
        this.currentConditionalFilter.conditions.splice(index, 1);
      },
      closeMappingConfig() {
        this.enableMapping = false;
        this.openExtenalSources = true;
      },
      closeSourcesList() {
        this.openExtenalSources = false;
        this.saveMappingConfig();
      },
      validateAndSave() {
        const missingFields = this.sourceFields.filter(
          (field) =>
            field.required &&
            !this.externalSourceConfiguration.mappingConfig[field.applicationFieldKey]
        );
        if (missingFields.length > 0) {
          this.$message.error('Please map all required fields.');
          return;
        }
        this.saveMappingConfig();
        this.$set(this.block, 'external_source_mapping_config', this.externalSourceConfiguration);
        this.enableMapping = false;
      },
      saveMappingConfig() {
        this.$set(this.block, 'external_source_mapping_config', this.externalSourceConfiguration);
      },
    },
    watch: {
      'externalSourceConfiguration.application': {
        handler() {
          if (this.externalSourceConfiguration.application) {
            this.loadIntegrations();
          }
        },
      },
    },
  };
</script>

<style scoped>
  /* Tree structure */
  .tree-node {
    list-style: none;
    margin: 0;
    padding-left: 20px;
    position: relative;
  }

  .tree-node::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e5e7eb;
  }

  .tree-leaf {
    margin: 8px 0;
    display: flex;
    align-items: center;
    gap: 6px;
    position: relative;
  }

  .selection-mode {
    padding: 8px 0;
  }

  .selection-container {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 6px;
  }

  .type-btn {
    min-width: 100px;
  }

  /* Branches and action numbers */
  .tree-branch {
    margin: 12px 0;
    position: relative;
  }

  .action-number {
    background: #3b82f6;
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    flex-shrink: 0;
  }

  .icon {
    color: #3b82f6;
    font-size: 16px;
  }

  .label {
    font-weight: 500;
    color: #334155;
    font-size: 14px;
  }

  .condition-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    background: #f8fafc;
    border-radius: 6px;
  }

  .filter-details {
    margin: 8px 0;
    padding: 8px 12px;
    background: #f1f5f9;
    border-radius: 4px;
    border-left: 3px solid #3b82f6;
  }

  .filter-name {
    font-weight: 600;
    color: #1e293b;
    font-size: 13px;
  }

  .filter-description {
    color: #64748b;
    font-size: 12px;
    margin-top: 2px;
  }

  /* Branches inside conditions */
  .branch {
    margin-left: 16px;
    padding-left: 12px;
    border-left: 1px dashed #cbd5e1;
    margin-bottom: 12px;
    position: relative;
  }

  .branch-label {
    font-size: 12px;
    font-weight: bold;
    color: #64748b;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f8fafc;
    border-radius: 4px;
  }

  .branch-number {
    font-weight: bold;
    color: #1e293b;
  }

  .branch-action-count {
    font-size: 11px;
    color: #64748b;
    font-weight: normal;
    font-style: italic;
  }

  .branch-content {
    margin-left: 8px;
  }

  /* Buttons */
  .remove-btn {
    margin-left: auto;
    color: #ef4444;
  }

  .edit-btn,
  .remove-btn {
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px; /* Slightly rounded square for professional look */
    font-size: 16px;
    padding: 0;
    transition:
      background 0.2s,
      color 0.2s;
  }
  .edit-btn {
    background: #eff6ff; /* Light blue background */
    color: #2563eb;
  }
  .edit-btn:hover {
    background: #dbeafe;
  }

  /* Delete button - danger color */
  .remove-btn {
    background: #fef2f2; /* Light red background */
    color: #dc2626;
  }
  .remove-btn:hover {
    background: #fee2e2;
  }

  /* Ensure spacing between chip and buttons */
  .action-header .edit-btn,
  .action-header .remove-btn {
    margin-left: 8px;
    flex-shrink: 0;
  }

  .add-action-btn {
    margin-top: 8px;
  }

  .condition-controls {
    margin-top: 12px;
    display: flex;
    gap: 10px;
    padding: 8px 12px;
    background: #f8fafc;
    border-radius: 6px;
    align-items: center;
  }

  .total-actions-count {
    margin-left: auto;
    font-size: 12px;
    font-weight: 600;
    color: #3b82f6;
    background: white;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
  }

  /* Messages */
  .no-condition-message {
    padding: 16px;
    text-align: center;
    color: #64748b;
    font-style: italic;
    background: #f8fafc;
    border-radius: 6px;
    margin: 8px 0;
  }

  /* Action header and chip */
  .action-header {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    flex-wrap: wrap;
  }

  .action-summary-chip {
    flex: 1;
    min-width: 0;
    max-width: 100%;
    display: flex;
    flex-wrap: wrap;
  }

  /* Drawer and action configuration */
  .drawer-content,
  .action-config-base {
    padding: 16px;
  }

  /* Selected action inside drawer */
  .selected-action-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #f1f5f9;
    border-radius: 6px;
    margin-bottom: 12px;
  }

  .selected-action-label {
    font-weight: 600;
    color: #1e293b;
  }

  /* Animations */
  .action-block,
  .condition-block {
    animation: fadeIn 0.3s ease-in;
  }

  .invalid-action {
    border: 1px solid #f87171; /* red border */
    background-color: #fef2f2; /* light red background */
    animation: highlightAction 0.5s ease-in-out;
  }

  @keyframes highlightAction {
    0% {
      background-color: #fef2f2;
    }
    50% {
      background-color: #fee2e2;
    }
    100% {
      background-color: #fef2f2;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>

<style>
  /* High z-index dialogs and poppers */
  .high-z-index-dialog {
    z-index: 4000 !important;
  }

  .high-z-index-dialog .el-dialog__wrapper,
  .high-z-index-dialog .dialog-wrapper {
    z-index: 4000 !important;
  }

  .high-z-index-dialog .el-dialog {
    z-index: 4001 !important;
  }

  .high-z-index-dialog .v-modal {
    z-index: 3999 !important;
  }

  .el-select-dropdown,
  .el-dropdown-menu,
  .el-popper,
  .el-tooltip__popper {
    z-index: 5000 !important;
  }
</style>
